import { Link } from "react-router-dom";
import Container from "../components/layout/Container";
import PageHeader from "../components/layout/PageHeader";
import AreaIcon from "../components/ui/AreaIcon";
import MediaCard from "../components/ui/MediaCard";
import VideoStack from "../components/ui/VideoStack";
import TextLink from "../components/ui/TextLink";
import { companies, isListed, projects, technology } from "../data/site";

const technologyProjects = projects.filter((project) => project.enabled !== false && project.technology);

export default function TechnologyPage() {
  const courseVideo = technology.videos.items.find(
    (item) => item.enabled !== false && item.href && item.page !== false
  );

  return (
    <>
      <PageHeader title="Technology" lede={technology.intro} mark />
      <Container className="pb-24">
        <section className="mt-4 md:mt-8">
          <h2 className="kicker">Areas</h2>
          <dl className="tech-areas mt-6 border-y border-[var(--line)]">
            {technology.areas.map((area, index) => (
              <div
                key={area.title}
                className="grid gap-1 border-b border-[var(--line)] py-5 last:border-b-0 sm:grid-cols-12 sm:gap-6"
              >
                <dt className="flex items-center font-medium text-[var(--ink)] sm:col-span-4">
                  <span className="mr-3 text-sm text-[var(--kicker)]">{String(index + 1).padStart(2, "0")}</span>
                  <AreaIcon title={area.title} size={16} />
                  <span className="ml-2">{area.title}</span>
                </dt>
                <dd className="text-[var(--muted)] sm:col-span-8">{area.text}</dd>
              </div>
            ))}
          </dl>
        </section>

        <section id="companies" className="mt-16 md:mt-24">
          <h2 className="kicker">Companies</h2>
          <div className="mt-6 grid border-y border-[var(--line)] md:grid-cols-2 md:divide-x md:divide-[var(--line)]">
            {companies.map((company) => (
              <article key={company.name} className="border-b border-[var(--line)] py-8 last:border-b-0 md:border-b-0 md:px-8 md:first:pl-0 md:last:pr-0">
                <p className="text-sm text-[var(--muted)]">{company.role}</p>
                <h3 className="mt-2 text-2xl font-medium tracking-tight text-[var(--ink)]">
                  {company.name}
                </h3>
                <p className="mt-4 max-w-md leading-relaxed text-[var(--text)]">{company.text}</p>
                <div className="mt-3">
                  <TextLink href={company.href} external>
                    {company.linkLabel}
                  </TextLink>
                </div>
                {company.email ? (
                  <a
                    className="text-sm text-[var(--muted)] underline decoration-[var(--line-strong)] underline-offset-4 hover:text-[var(--ink)]"
                    href={`mailto:${company.email}`}
                  >
                    {company.email}
                  </a>
                ) : null}
              </article>
            ))}
          </div>
        </section>

        {isListed(technology.products) ? (
          <section className="mt-16 border-t border-[var(--line)] pt-10 md:mt-24">
            <h2 className="kicker">{technology.products.title}</h2>
            <div className="mt-6 grid gap-4 md:grid-cols-2">
              {technology.products.items
                .filter((item) => item.enabled !== false && item.page !== false)
                .map((item) => (
                  <MediaCard
                    key={item.title}
                    href={item.href}
                    image={item.image}
                    kicker={item.title}
                    title={item.text}
                    action={item.action}
                    poster={item.poster || "tech"}
                  />
                ))}
            </div>
            <VideoStack items={technology.products.videos} poster="tech" place="page" />
          </section>
        ) : null}

        {isListed(technology.teaching) ? (
          <section className="mt-16 border-t border-[var(--line)] pt-10 md:mt-24">
            <h2 className="kicker">{technology.teaching.title}</h2>
            <div className="mt-6 grid gap-4 md:grid-cols-2">
              {technology.teaching.items
                .filter((item) => item.enabled !== false && item.href && item.page !== false)
                .map((item) => (
                  <MediaCard
                    key={item.title}
                    href={item.href}
                    image={item.image}
                    kicker={technology.teaching.title}
                    title={item.title}
                    action={item.action}
                    poster={item.poster || "tech"}
                  />
                ))}
            </div>
          </section>
        ) : null}

        {isListed(technology.videos) ? (
          <section className="mt-16 border-t border-[var(--line)] pt-10 md:mt-24">
            <h2 className="kicker">{technology.videos.title}</h2>
            <VideoStack items={technology.videos.items} poster="tech" place="page" />
          </section>
        ) : null}

        {isListed(technology.course) ? (
          <section className="mt-16 border-t border-[var(--line)] pt-10 md:mt-24">
            <h2 className="kicker">{technology.course.title}</h2>
            <div className="mt-6 grid gap-4 md:grid-cols-2">
              <MediaCard
                href={technology.course.href}
                image={technology.course.image}
                kicker={technology.course.title}
                title={technology.course.text}
                action={technology.course.action}
                poster={technology.course.poster}
              />
              {courseVideo ? (
                <MediaCard
                  href={courseVideo.href}
                  image={courseVideo.image}
                  kicker={courseVideo.label}
                  title={courseVideo.title}
                  action={courseVideo.action}
                  poster="tech"
                  video
                />
              ) : null}
            </div>
          </section>
        ) : null}

        {technologyProjects.length ? (
          <section className="mt-16 border-t border-[var(--line)] pt-10 md:mt-24">
            <h2 className="kicker">Projects</h2>
            <div className="mt-6 grid gap-12">
              {technologyProjects.map((project) => (
                <div key={project.title} className="grid items-center gap-6 md:grid-cols-2">
                  <Link to={project.href} className="project-shot" aria-label={project.title}>
                    {project.image ? (
                      <img src={project.image} alt={project.title} />
                    ) : (
                      <span className="project-poster" />
                    )}
                  </Link>
                  <div>
                    <p className="text-2xl font-medium tracking-tight text-[var(--ink)]">{project.title}</p>
                    <p className="mt-3 leading-relaxed text-[var(--text)]">{project.summary}</p>
                    <div className="mt-2">
                      <TextLink href="/projects">View projects</TextLink>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>
        ) : null}
      </Container>
    </>
  );
}
